import { FC, useEffect, useMemo } from "react";
import RelatedCargoRailyBillingTableRow from "./types/related-cargo-raily-billing-table-row";
import relatedCargoRailyBillingsHelper from "./related-cargo-raily-billings.helper";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import CardComponent from "../../../../../../common/components/card/card.component";
import TableComponent from "../../../../../../common/components/table/table.component";
import dateService from "../../../../../../common/utils/date/date.service";
import billingsTranslationsHelper from "../../../../../../languages/billings-translations.helper";
import billingRoutesHelper from "../../../../common/routes/billing-routes.helper";
import TableLinkButtonComponent from "../../../../../../common/components/table/button/link/table-link-button.component";
import useBillingsOrderRelatedCargoRaily from "../../../../../../common/services/billings/order/related/cargo-raily/use-billings-order-related-cargo-raily";
import useAbort from "../../../../../../common/hooks/use-abort";
import BillingsOrderRelatedCargoRailyParams from "../../../../../../common/services/billings/order/related/cargo-raily/billings-order-related-cargo-raily-params";
import BillingsOrderRelatedCargoRaily from "../../../../../../common/services/billings/order/related/cargo-raily/billings-order-related-cargo-raily";

type RelatedCargoRailyBillingsProps = {
  orderId: string | undefined;
};

const RelatedCargoRailyBillingsComponent: FC<RelatedCargoRailyBillingsProps> = (
  props,
) => {
  const billingsOrderRelatedCargoRaily = useBillingsOrderRelatedCargoRaily();
  const billingsOrderRelatedCargoRailyAbort = useAbort();

  useEffect(() => {
    if (props.orderId) {
      const params: BillingsOrderRelatedCargoRailyParams = {
        orderUuid: props.orderId,
      };

      billingsOrderRelatedCargoRaily.load(
        params,
        billingsOrderRelatedCargoRailyAbort.signal,
      );

      billingsOrderRelatedCargoRailyAbort.revoke;
    }
  }, [props.orderId]);

  const createTableRow = (
    billingData: BillingsOrderRelatedCargoRaily,
  ): RelatedCargoRailyBillingTableRow => {
    return {
      id: billingData.cargoOrder.humanId.toString(),
      value: {
        date: (
          <div title={dateService.format(billingData.date, "dd/mm/yyyy")}>
            {dateService.format(billingData.date, "dd/mm/yyyy")}
          </div>
        ),
        internalOrderId: (
          <div title={billingData.cargoOrder.humanId.toString()}>
            {billingData.cargoOrder.humanId}
          </div>
        ),
        companyName: (
          <div title={billingData.contract.cargoCompany.companyName}>
            {billingData.contract.cargoCompany.companyName}
          </div>
        ),
        model: (
          <div title={billingData.contract.model}>
            {billingData.contract.model}
          </div>
        ),
        rate: (
          <div title={billingData.contract.distanceRate.toString()}>
            {billingData.contract.distanceRate.toFixed(2)} PLN
          </div>
        ),
        distance: (
          <div title={billingData.distance.toString()}>
            {billingData.distance}
          </div>
        ),
        routeDistanceCost: (
          <div title={billingData.discountAmount.toString()}>
            {billingData.discountAmount.toFixed(2)} PLN
          </div>
        ),
        discount: (
          <div title={billingData.discount.toString()}>
            {billingData.discount.toFixed(2)}%
          </div>
        ),
        discountTotal: (
          <div title={billingData.discountAmount.toString()}>
            {billingData.discountAmount.toFixed(2)} PLN
          </div>
        ),
        haltingCost: (
          <div title={billingData.haltingAmount.toString()}>
            {billingData.haltingAmount.toFixed(2)} PLN
          </div>
        ),
        highwayCost: (
          <div title={billingData.highwayCharge.toString()}>
            {billingData.highwayCharge.toFixed(2)} PLN
          </div>
        ),
        total: (
          <div title={billingData.totalAmount.toString()}>
            {billingData.totalAmount.toFixed(2)} PLN
          </div>
        ),
        actions: (
          <TableLinkButtonComponent
            openInNewTab
            icon={faEdit}
            to={billingRoutesHelper.getCargoWithRailyAddRoute({
              orderUuid: billingData.cargoOrder.uuid,
            })}
            type="primary"
            idForTesting={"cargo-with-raily-related-billing-button"}
            classNames={{
              root: "related_order_billings_table_edit_billing_icon",
            }}
          />
        ),
      },
    };
  };

  const tableColumns = relatedCargoRailyBillingsHelper.getTableColumns();

  const tableRows: RelatedCargoRailyBillingTableRow[] = useMemo(() => {
    if (billingsOrderRelatedCargoRaily.data) {
      return billingsOrderRelatedCargoRaily.data.map((listingItem) =>
        createTableRow(listingItem),
      );
    }

    return [];
  }, [billingsOrderRelatedCargoRaily.data]);

  const translations =
    billingsTranslationsHelper.getRailyTaxiAddBillingsTranslations()
      .relatedBillings.cargo_raily;

  return (
    <CardComponent
      classNames={{ root: "related_order_billings" }}
      header={{ title: translations.summaryLabel }}
    >
      <TableComponent
        isLoading={billingsOrderRelatedCargoRaily.isLoading}
        columns={tableColumns}
        rows={tableRows}
      />
    </CardComponent>
  );
};

export default RelatedCargoRailyBillingsComponent;
